// client/src/services/desks.ts
// Client for the admin-only Desks & Assignments board (/api/desks + /api/desks-assign).

export interface DeskPerson {
  staffIndex: number;
  name: string;
  officeCode: string;
  office: string;
  dept: string;
}

export type Lane = 'admin_pool' | 'inbox_triage' | 'open' | 'in_progress' | 'completed';

export type StepStatus = 'pending' | 'active' | 'done';

export interface DeskJob {
  jobIdx: number;
  staffIndex: number;   // "Assigned To" — a person's StaffIndex or the Admin Pool
  officeCode: string;
  office: string;
  dept: string;
  jobClass: string;
  jobName: string;
  jobCode: string;
  clientCode: string;
  clientName: string;
  createdAt: string;      // PE Job_Created — the FIFO ordering key
  currentDueDate: string; // PE tblJob_TaxReturn.CurrentDueDate (extension-aware); '' if none
  assignedAt: string;     // when the job was assigned to the current person
  assignedBy: string;     // admin-hq: email of who made the current assignment ('' if from PE)
  isPaper: boolean;       // derived: the return has a paper-filed jurisdiction (PE PaperFile)
  deliveryMode: '' | 'electronic' | 'paper'; // PE Assembly/Shipping questionnaire (Assembly Instructions: ELECT/PAPER); '' = not set
  assemblyMethod: string; // raw Assembly Instructions answer (ELECT / PAPER)
  shippingMethod: string; // raw Shipping Instructions answer (SAFESEND, SHAREFILE, MEMBER, FEDUPS, …)
  assemblyStatus: StepStatus; // derived from the PE work status
  shippingStatus: StepStatus;
  peStage: '' | 'assembly' | 'shipping' | 'binder'; // PE-derived stage; drives the card's colored status chip
  sowUnsigned: boolean;   // PE-derived: on the "Missing Signed MSEL or SOW" hold (needs a signed SOW/MSEL)
  workStatusDesc: string; // PE work-status description (tooltip / PE-stage note)
  lane: Lane;             // admin-hq-managed board lane (not a PE field)
  laneUpdatedAt: string;  // when the lane was last set — drives auto-archive of Completed
  laneUpdatedBy: string;  // admin-hq: email of who last moved the lane ('' if never moved)
  notes: string;          // admin-hq board notes ('' if none)
  notesUpdatedBy: string; // email of who last edited the notes
  notesUpdatedAt: string; // ISO timestamp of the last notes edit
}

export interface DesksData {
  poolIndex: number;    // StaffIndex of the Admin Pool desk
  people: DeskPerson[];
  jobs: DeskJob[];
  peBase: string;       // Practice Engine web base, for per-job "Open in PE" links
}

export async function fetchDesks(): Promise<DesksData> {
  const r = await fetch('/api/desks', { credentials: 'include', cache: 'no-store' });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    throw new Error(b.error || `Server error ${r.status}`);
  }
  return r.json();
}

// Set who's working a job ("Assigned To" — a person's StaffIndex, or the Admin Pool).
// Writes PE Job_CurrentStaff.
export async function assignJob(jobIdx: number, toStaffIndex: number): Promise<void> {
  const r = await fetch('/api/desks-assign', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jobIdx, toStaffIndex }),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    throw new Error(b.error || `Server error ${r.status}`);
  }
}

// Set a job's board lane (admin-hq-managed workflow status).
export async function setDeskStatus(jobIdx: number, status: Lane): Promise<void> {
  const r = await fetch('/api/desks-status', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jobIdx, status }),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    throw new Error(b.error || `Server error ${r.status}`);
  }
}

// Save the notes on a board card (the Dev Team-style per-card Notes). kind 'job' → id is
// the PE Job_Idx; kind 'email' → id is the inbox email's bugReportId. '' clears.
export interface SavedNotes { notes: string; notesUpdatedBy: string | null; notesUpdatedAt: string | null }
export async function saveDeskNotes(kind: 'job' | 'email', id: number, notes: string): Promise<SavedNotes> {
  const r = await fetch('/api/desks-notes', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ kind, id, notes }),
  });
  const b = await r.json().catch(() => ({}));
  if (!r.ok || b?.error) throw new Error(b?.error || `Server error ${r.status}`);
  return { notes: b.notes ?? '', notesUpdatedBy: b.notesUpdatedBy ?? null, notesUpdatedAt: b.notesUpdatedAt ?? null };
}
